/**
 * A state, and a proportion.
 *
 * `StatusDot` pairs a coloured dot with its label, and the label is not
 * optional. A dot alone is invisible to anyone with a colour deficiency, gone
 * when the row is copied into a ticket, and unreadable in a screenshot —
 * which is how most of these end up being discussed.
 *
 * `Meter` is a proportion with its number beside it, for the same reason: a
 * bar at 80% and a bar at 95% are the same picture at a glance.
 */

import "./data.css";

export type StatusTone = "neutral" | "success" | "warning" | "danger" | "info" | "accent";

export interface StatusDotProps {
  label: string;
  tone?: StatusTone;
}

export function StatusDot({ label, tone = "neutral" }: StatusDotProps) {
  return (
    <span className="hardauto-status" data-tone={tone}>
      <span className="hardauto-status__dot" aria-hidden="true" />
      {label}
    </span>
  );
}

export interface MeterProps {
  /** 0 to 100. Values outside are clamped rather than overflowing the track. */
  value: number;
  /** Rendered beside the bar. Omit to show the percentage. */
  text?: string;
  tone?: StatusTone;
  /** Announced as the meter's name. */
  label: string;
  /**
   * Fill the container instead of sitting inline.
   *
   * The default is compact, for a meter in a table cell beside other content.
   * A gauge that *is* the point of its panel — a credit line under the figure
   * it measures — needs the width, and without this it collapsed to a 33px
   * stub next to its own label while correctly reporting 100 %: a bar that
   * says "full" and looks nearly empty.
   */
  block?: boolean;
}

export function Meter({ value, text, tone = "accent", label, block = false }: MeterProps) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));

  return (
    <span className="hardauto-meter" data-tone={tone} data-block={block ? "true" : undefined}>
      {/* `role="meter"` rather than `progressbar`: this reports a measurement,
          not the progress of a task the user is waiting for. Screen readers
          announce them differently, and "loading 80%" is wrong for a
          completeness score. */}
      <span
        className="hardauto-meter__track"
        role="meter"
        aria-label={label}
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <span className="hardauto-meter__fill" style={{ inlineSize: `${clamped}%` }} />
      </span>
      <span className="hardauto-meter__text">{text ?? `${clamped}%`}</span>
    </span>
  );
}
